/*
  Mobile add-to-cart bar (block "mobile-atc-bar").
  Fixed bottom bar on mobile with price, one <select> per product option and an
  add-to-cart button. Kept in sync with the main product variant picker, adds to
  cart via AJAX, refreshes the cart drawer and opens it.
*/
if (!customElements.get('mobile-atc-bar')) {

  class MobileAtcBar extends HTMLElement {

    constructor() {
      super();
      this.productHandle = this.dataset.productHandle;
      this.soldOutLabel = this.dataset.soldOutLabel || '';
      this.errorLabel = this.dataset.errorLabel || '';
      this.optionInputs = this.querySelectorAll('[data-option-index]');
      this.addToCartButton = this.querySelector('[data-add-to-cart]');
      this.priceElement = this.querySelector('[data-price]');
      this.comparePriceElement = this.querySelector('[data-compare-price]');
      this.iconElement = this.querySelector('[data-icon]');
      this.loadingElement = this.querySelector('[data-loading]');
      this.currentVariant = null;
      this.productData = null;
      this.onMainVariantChange = this.handleMainVariantChange.bind(this);
    }

    connectedCallback() {
      this.fetchProductData();
      this.setupEventListeners();
      document.addEventListener('krown:variant:change', this.onMainVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('krown:variant:change', this.onMainVariantChange);
    }

    async fetchProductData() {
      try {
        const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}products/${this.productHandle}.js`);
        this.productData = await response.json();
        this.updateCurrentVariant();
        this.updateOptionAvailability();
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    }

    setupEventListeners() {
      this.optionInputs.forEach(input => {
        if (input.tagName === 'SELECT') {
          input.addEventListener('change', event => this.handleVariantChange(event.target));
        }
      });
      this.addToCartButton.addEventListener('click', () => this.handleAddToCart());
    }

    handleVariantChange(changedSelect) {
      this.updateCurrentVariant();
      this.updatePrice();
      this.updateOptionAvailability();
      if (changedSelect) {
        this.syncMainPicker(parseInt(changedSelect.dataset.optionIndex, 10), changedSelect.value);
      }
    }

    // Mark sold-out values in each <select>. A colour only looks sold out when it
    // has no available variant at all; other options are checked against the
    // current selection. Native mobile pickers ignore text-decoration, so the
    // label suffix is what makes the state visible everywhere.
    updateOptionAvailability() {
      if (!this.productData) return;

      const selections = {};
      this.optionInputs.forEach(input => {
        selections[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });

      this.optionInputs.forEach(input => {
        if (input.tagName !== 'SELECT') return;
        const optionIndex = parseInt(input.dataset.optionIndex, 10);
        const isColorOption = input.dataset.optionRole === 'color';

        [...input.options].forEach(option => {
          if (option.dataset.label === undefined) {
            option.dataset.label = option.textContent.trim();
          }

          let available;
          if (isColorOption) {
            available = this.productData.variants.some(v => v[`option${optionIndex}`] === option.value && v.available);
          } else {
            const candidate = { ...selections, [optionIndex]: option.value };
            const variant = this.productData.variants.find(v =>
              (candidate[1] === undefined || v.option1 === candidate[1]) &&
              (candidate[2] === undefined || v.option2 === candidate[2]) &&
              (candidate[3] === undefined || v.option3 === candidate[3])
            );
            available = !!(variant && variant.available);
          }

          option.style.textDecoration = available ? '' : 'line-through';
          option.textContent = available ? option.dataset.label : `${option.dataset.label} - ${this.soldOutLabel}`;
        });
      });
    }

    // Push a selection made in this bar to the main product variant picker
    syncMainPicker(optionIndex, value) {
      const mainPicker = document.querySelector('[data-main-product-page-variants]');
      if (!mainPicker) return;

      const block = mainPicker.querySelectorAll('[data-js-product-variant]')[optionIndex - 1];
      if (!block) return;

      const radio = [...block.querySelectorAll('input[type="radio"]')].find(input => input.value === value);
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const select = block.querySelector('select');
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Reflect a selection made in the main product variant picker in this bar
    handleMainVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !this.productData) return;

      const match = this.productData.variants.find(v => v.id === variant.id);
      if (!match) return;

      this.currentVariant = match;
      this.optionInputs.forEach(input => {
        if (input.tagName !== 'SELECT') return;
        const newValue = match[`option${parseInt(input.dataset.optionIndex, 10)}`];
        if (newValue !== undefined && input.value !== newValue) {
          input.value = newValue;
        }
      });

      this.updatePrice();
      this.updateOptionAvailability();
      this.addToCartButton.disabled = !this.currentVariant.available;
    }

    updateCurrentVariant() {
      if (!this.productData) return;

      const selected = [];
      this.optionInputs.forEach(input => {
        selected[parseInt(input.dataset.optionIndex, 10) - 1] = input.value;
      });

      this.currentVariant = this.productData.variants.find(variant =>
        variant.option1 === selected[0] &&
        (selected[1] === undefined || variant.option2 === selected[1]) &&
        (selected[2] === undefined || variant.option3 === selected[2])
      );

      if (this.currentVariant) {
        this.addToCartButton.disabled = !this.currentVariant.available;
      }
    }

    updatePrice() {
      if (!this.currentVariant || !this.priceElement) return;

      this.priceElement.textContent = this.formatMoney(this.currentVariant.price);

      if (this.comparePriceElement) {
        const compare = this.currentVariant.compare_at_price;
        if (compare && compare > this.currentVariant.price) {
          this.comparePriceElement.textContent = this.formatMoney(compare);
          this.comparePriceElement.style.display = '';
        } else {
          this.comparePriceElement.style.display = 'none';
        }
      }
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: this.dataset.currency
      }).format(cents / 100);
    }

    async handleAddToCart() {
      if (!this.currentVariant || !this.currentVariant.available) return;

      this.setLoading(true);

      try {
        const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new URLSearchParams({ id: this.currentVariant.id, quantity: 1 }).toString()
        });
        const responseData = await response.json();

        if (response.ok && responseData.status != 422) {
          // Refresh the cart drawer the same way the theme does
          const text = await (await fetch('?section_id=helper-cart')).text();
          const section = new DOMParser().parseFromString(text, 'text/html');
          const cartItems = document.getElementById('AjaxCartForm');

          if (cartItems) {
            cartItems.innerHTML = section.getElementById('AjaxCartForm').innerHTML;
            if (cartItems.ajaxifyCartItems) {
              cartItems.ajaxifyCartItems();
            }
            document.getElementById('AjaxCartSubtotal').innerHTML = section.getElementById('AjaxCartSubtotal').innerHTML;

            const count = document.querySelector('#AjaxCartForm [data-cart-count]').textContent;
            document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
              elm.textContent = count;
            });
            document.querySelectorAll('[data-cart-count]').forEach(elm => {
              elm.textContent = count;
              if (parseInt(count, 10) > 0) {
                elm.classList.remove('hide', 'hidden');
              }
            });
            document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
              elm.textContent = document.querySelector('#AjaxCartForm [data-cart-total]').textContent;
            });
          }

          const productForm = document.querySelector('product-form');
          if (productForm) {
            productForm.dispatchEvent(new Event('add-to-cart'));
          }

          await new Promise(resolve => setTimeout(resolve, 150));

          const cartHandle = document.getElementById('site-cart-handle');
          if (cartHandle) {
            cartHandle.click();
          }
        } else {
          alert(responseData.description || responseData.message || this.errorLabel);
        }
      } catch (error) {
        console.error('Error:', error);
        alert(this.errorLabel);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.iconElement.style.display = isLoading ? 'none' : '';
      this.loadingElement.style.display = isLoading ? 'block' : 'none';
      this.addToCartButton.disabled = isLoading ? true : !(this.currentVariant && this.currentVariant.available);
    }
  }

  customElements.define('mobile-atc-bar', MobileAtcBar);
}
